import { useSuspenseQuery } from "@tanstack/react-query"
import {
  sortAdults,
  sortChildren,
} from "source/myChurchCenter/Profile/households/Household"
import { useSession } from "source/shared/hooks/useSession"
import { Household, HouseholdMember, Person } from "../types/api"

type ResponseData = Household[]
type Included = Array<Person | HouseholdMember>

export function useHouseholds() {
  const {
    data: { id: currentPersonId },
  } = useSession(true)

  return useSuspenseQuery({
    queryKey: [
      `/people/v2/people/${currentPersonId}/households?include=people,household_members`,
    ],
    select: ({
      data,
      included,
    }: {
      data: ResponseData
      included: Included
    }) => {
      const includedHouseholdRecords = included.filter(
        (record) => record.type === "HouseholdMember",
      )
      const includedPersonRecords = included.filter(
        (record) => record.type === "Person",
      )

      return data.map((household) => {
        const {
          id,
          attributes: { name },
        } = household

        const members = household.relationships.people.data.map((person) => {
          const currentPerson = includedPersonRecords.find(
            (includedMember) => includedMember.id === person.id,
          )! as Person

          const associatedHouseholdRecord = includedHouseholdRecords.find(
            (member) =>
              (member as HouseholdMember).relationships.person.data.id ===
                person.id &&
              household.relationships.household_members.data.find(
                (hhm) => hhm.id === member.id,
              ),
          ) as HouseholdMember
          const { birthday, child, demographic_avatar_url, name, status } =
            currentPerson.attributes

          return {
            birthday,
            child,
            demographic_avatar_url,
            householdMemberId: associatedHouseholdRecord?.id,
            householdRole: associatedHouseholdRecord.attributes.household_role,
            link: associatedHouseholdRecord.links.self,
            personId: person.id,
            name,
            status,
          }
        })
        const currentUser = members.find(
          (member) => member.personId === currentPersonId,
        )
        const adults = members.filter(
          (member) => !member.child && member.personId !== currentPersonId,
        )
        const children = members.filter(
          (member) => member.child && member.personId !== currentPersonId,
        )
        const allHouseholdRoles = household.relationships.household_members.data
          .map((hhm) => {
            const member = includedHouseholdRecords.find(
              (hhrecord) => hhrecord.id === hhm.id,
            ) as HouseholdMember

            return member
              ? {
                  id: member.id,
                  household_role: member.attributes.household_role,
                  name: member.attributes.person_name,
                }
              : null
          })
          .filter((role): role is NonNullable<typeof role> => role !== null)
        return {
          id,
          householdMembers: [
            currentUser,
            ...sortAdults(adults),
            ...sortChildren(children),
          ],
          householdRoles: allHouseholdRoles,
          householdName: name,
        }
      })
    },
  })
}
